import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import SweetAlert from '../../../components/sweetalert/SweetAlert';
import * as types from './scheduleViewerActionType';
import * as actions from './scheduleViewerActions';
import * as services from './scheduleViewerServices';

function* fetchShiftsSaga(action) {
    try {
        const response = yield call(services.fetchShifts, action.payload);
        if (response?.success) {
            yield put(actions.fetchShiftsSuccess(response.values || response.value || []));
        } else {
            const err = response?.errorMessage || 'Failed to load shifts';
            yield put(actions.fetchShiftsFailure(err));
            SweetAlert.error({ title: 'Error', text: err });
        }
    } catch (error) {
        const err = error.message || 'Failed to load shifts';
        yield put(actions.fetchShiftsFailure(err));
        SweetAlert.error({ title: 'Error', text: err });
    }
}

function* fetchScheduleDropdownsSaga() {
    try {
        const [rads, worklists, sites, states, tasks] =
            yield call(services.fetchScheduleDropdowns);
        yield put(actions.fetchScheduleDropdownsSuccess({
            radiologists: (rads?.values?.RadiologistDropdownItems || []).map(r => ({ RadiologistID: r.Id, RadiologistName: r.Name })),
            worklists: worklists?.values?.listWorkList || [],
            sites: sites?.values?.listSite || [],
            states: states?.values?.lstState || [],
            tasks: tasks?.values?.taskList || [],
        }));
    } catch (error) {
        yield put(actions.fetchScheduleDropdownsFailure(error.message || 'Failed to load dropdowns'));
    }
}

function* saveShiftSaga(action) {
    try {
        const response = yield call(services.saveShift, action.payload);
        if (response?.success) {
            const result = response.value || {};
            if (result.Conflicts && result.Conflicts.length > 0) {
                yield put(actions.saveShiftFailure(null));
                yield put(actions.setConflictPreview({
                    conflicts: result.Conflicts,
                    skipped: result.Skipped || [],
                    targetCount: result.TargetCount ?? result.Conflicts.length,
                    actionType: types.SAVE_SHIFT_REQUEST,
                    payload: action.payload,
                }));
                return;
            }
            yield put(actions.saveShiftSuccess());
            yield put(actions.clearSelectedShift());
            SweetAlert.success({ text: 'Shift saved.' });
            const lastParams = yield select(state => state.scheduleViewer.shifts.lastFetchParams);
            if (lastParams) {
                yield put(actions.fetchShiftsRequest(lastParams));
            }
        } else {
            const err = response?.errorMessage || 'Failed to save shift';
            yield put(actions.saveShiftFailure(err));
            SweetAlert.error({ text: err });
        }
    } catch (error) {
        const err = error.message || 'Failed to save shift';
        yield put(actions.saveShiftFailure(err));
        SweetAlert.error({ text: err });
    }
}

function* saveShiftBulkSaga(action) {
    try {
        const response = yield call(services.saveShiftBulk, action.payload);
        if (response?.success) {
            const result = response.value || response.values || { CreatedShiftIDs: [], Skipped: [] };
            if (result.Conflicts && result.Conflicts.length > 0) {
                yield put(actions.saveShiftBulkFailure(null));
                yield put(actions.setConflictPreview({
                    conflicts: result.Conflicts,
                    skipped: result.Skipped || [],
                    targetCount: result.TargetCount ?? result.Conflicts.length,
                    actionType: types.SAVE_SHIFT_BULK_REQUEST,
                    payload: action.payload,
                }));
                return;
            }
            yield put(actions.saveShiftBulkSuccess(result));
            const created = (result.CreatedShiftIDs || []).length;
            const skipped = (result.Skipped || []).length;
            const total = created + skipped;
            if (skipped === 0) {
                yield put(actions.clearSelectedShift());
                SweetAlert.success({ text: `Created ${created} shift${created === 1 ? '' : 's'}.` });
            } else {
                SweetAlert.success({ text: `Created ${created} of ${total} shifts. ${skipped} skipped.` });
            }
            const lastParams = yield select(state => state.scheduleViewer.shifts.lastFetchParams);
            if (lastParams) {
                yield put(actions.fetchShiftsRequest(lastParams));
            }
        } else {
            const err = response?.errorMessage || 'Failed to save shifts';
            yield put(actions.saveShiftBulkFailure(err));
            SweetAlert.error({ text: err });
        }
    } catch (error) {
        const err = error.message || 'Failed to save shifts';
        yield put(actions.saveShiftBulkFailure(err));
        SweetAlert.error({ text: err });
    }
}

function* fetchRadsAssociatedSitesSaga(action) {
    try {
        const { radiologistIds, asOfDate } = action.payload;
        if (!radiologistIds?.length) {
            yield put(actions.fetchRadsAssociatedSitesSuccess({}));
            return;
        }
        const responses = yield all(
            radiologistIds.map(id => call(services.getRadAssociatedSites, id, asOfDate))
        );
        const sitesByRad = {};
        radiologistIds.forEach((id, i) => {
            const r = responses[i];
            sitesByRad[id] = r?.success ? (r.values || r.value || []) : [];
        });
        yield put(actions.fetchRadsAssociatedSitesSuccess(sitesByRad));
    } catch (error) {
        yield put(actions.fetchRadsAssociatedSitesFailure(error.message || 'Failed to load associated sites'));
    }
}

function* deleteShiftSaga(action) {
    try {
        const response = yield call(services.deleteShift, action.payload);
        if (response?.success) {
            yield put(actions.deleteShiftSuccess());
            yield put(actions.clearSelectedShift());
            SweetAlert.success({ text: 'Shift deleted.' });
            const lastParams = yield select(state => state.scheduleViewer.shifts.lastFetchParams);
            if (lastParams) {
                yield put(actions.fetchShiftsRequest(lastParams));
            }
        } else {
            const err = response?.errorMessage || 'Failed to delete shift';
            yield put(actions.deleteShiftFailure(err));
            SweetAlert.error({ text: err });
        }
    } catch (error) {
        const err = error.message || 'Failed to delete shift';
        yield put(actions.deleteShiftFailure(err));
        SweetAlert.error({ text: err });
    }
}

function* updateShiftGroupSaga(action) {
    try {
        const response = yield call(services.updateShiftGroup, action.payload);
        if (response?.success) {
            const result = response.value || {};
            if (result.Conflicts && result.Conflicts.length > 0) {
                yield put(actions.updateShiftGroupFailure(null));
                yield put(actions.setConflictPreview({
                    conflicts: result.Conflicts,
                    skipped: result.Skipped || [],
                    targetCount: result.TargetCount ?? result.Conflicts.length,
                    actionType: types.UPDATE_SHIFT_GROUP_REQUEST,
                    payload: action.payload,
                }));
                return;
            }
            const rows = result.rows ?? 0;
            const skipped = (result.Skipped || []).length;
            yield put(actions.updateShiftGroupSuccess());
            yield put(actions.clearSelectedShift());
            SweetAlert.success({
                text: skipped > 0
                    ? `Updated ${rows} linked shift${rows === 1 ? '' : 's'}. ${skipped} skipped (conflict).`
                    : `Updated ${rows} linked shift${rows === 1 ? '' : 's'}.`,
            });
            const lastParams = yield select(state => state.scheduleViewer.shifts.lastFetchParams);
            if (lastParams) {
                yield put(actions.fetchShiftsRequest(lastParams));
            }
        } else {
            const err = response?.errorMessage || 'Failed to update linked shifts';
            yield put(actions.updateShiftGroupFailure(err));
            SweetAlert.error({ text: err });
        }
    } catch (error) {
        const err = error.message || 'Failed to update linked shifts';
        yield put(actions.updateShiftGroupFailure(err));
        SweetAlert.error({ text: err });
    }
}

function* swapShiftSaga(action) {
    try {
        const response = yield call(services.swapShift, action.payload);
        if (response?.success) {
            const result = response.value || response.values || {};
            if (result.Conflicts && result.Conflicts.length > 0) {
                yield put(actions.swapShiftFailure(null));
                yield put(actions.setConflictPreview({
                    conflicts: result.Conflicts,
                    skipped: result.Skipped || [],
                    targetCount: result.TargetCount ?? result.Conflicts.length,
                    actionType: types.SWAP_SHIFT_REQUEST,
                    payload: action.payload,
                }));
                return;
            }
            yield put(actions.swapShiftSuccess(result));
            yield put(actions.clearSelectedShift());
            const created = result.CreatedCount ?? (result.CreatedShiftIDs || []).length;
            const skipped = (result.Skipped || []).length;
            SweetAlert.success({
                text: skipped > 0
                    ? `Swapped shift (${created} created, ${skipped} skipped for conflict).`
                    : `Swapped shift (${created} new shift${created === 1 ? '' : 's'}).`,
            });
            const lastParams = yield select(state => state.scheduleViewer.shifts.lastFetchParams);
            if (lastParams) {
                yield put(actions.fetchShiftsRequest(lastParams));
            }
        } else {
            const err = response?.errorMessage || 'Failed to swap shift';
            yield put(actions.swapShiftFailure(err));
            SweetAlert.error({ text: err });
        }
    } catch (error) {
        const err = error.message || 'Failed to swap shift';
        yield put(actions.swapShiftFailure(err));
        SweetAlert.error({ text: err });
    }
}

function* swapShiftGroupSaga(action) {
    try {
        const response = yield call(services.swapShiftGroup, action.payload);
        if (response?.success) {
            const result = response.value || response.values || {};
            if (result.Conflicts && result.Conflicts.length > 0) {
                yield put(actions.swapShiftGroupFailure(null));
                yield put(actions.setConflictPreview({
                    conflicts: result.Conflicts,
                    skipped: result.Skipped || [],
                    targetCount: result.TargetCount ?? result.Conflicts.length,
                    actionType: types.SWAP_SHIFT_GROUP_REQUEST,
                    payload: action.payload,
                }));
                return;
            }
            yield put(actions.swapShiftGroupSuccess(result));
            yield put(actions.clearSelectedShift());
            const created = result.CreatedRows ?? (result.CreatedShiftIDs || []).length;
            const deleted = result.DeletedRows ?? 0;
            const skipped = (result.Skipped || []).length;
            SweetAlert.success({
                text: skipped > 0
                    ? `Swapped linked shifts (${deleted} removed, ${created} created, ${skipped} skipped for conflict).`
                    : `Swapped linked shifts (${deleted} removed, ${created} created).`,
            });
            const lastParams = yield select(state => state.scheduleViewer.shifts.lastFetchParams);
            if (lastParams) {
                yield put(actions.fetchShiftsRequest(lastParams));
            }
        } else {
            const err = response?.errorMessage || 'Failed to swap linked shifts';
            yield put(actions.swapShiftGroupFailure(err));
            SweetAlert.error({ text: err });
        }
    } catch (error) {
        const err = error.message || 'Failed to swap linked shifts';
        yield put(actions.swapShiftGroupFailure(err));
        SweetAlert.error({ text: err });
    }
}

function* deleteShiftGroupSaga(action) {
    try {
        const response = yield call(services.deleteShiftGroup, action.payload);
        if (response?.success) {
            const rows = response.value?.rows ?? 0;
            yield put(actions.deleteShiftGroupSuccess());
            yield put(actions.clearSelectedShift());
            SweetAlert.success({ text: `Deleted ${rows} linked shift${rows === 1 ? '' : 's'}.` });
            const lastParams = yield select(state => state.scheduleViewer.shifts.lastFetchParams);
            if (lastParams) {
                yield put(actions.fetchShiftsRequest(lastParams));
            }
        } else {
            const err = response?.errorMessage || 'Failed to delete linked shifts';
            yield put(actions.deleteShiftGroupFailure(err));
            SweetAlert.error({ text: err });
        }
    } catch (error) {
        const err = error.message || 'Failed to delete linked shifts';
        yield put(actions.deleteShiftGroupFailure(err));
        SweetAlert.error({ text: err });
    }
}

function* updateShiftNotesSaga(action) {
    try {
        const { shiftId, notes } = action.payload;
        const response = yield call(services.updateShiftNotes, shiftId, notes);
        if (response?.success) {
            yield put(actions.updateShiftNotesSuccess());
        } else {
            const err = response?.errorMessage || 'Failed to update notes';
            yield put(actions.updateShiftNotesFailure(err));
            SweetAlert.error({ text: err });
        }
    } catch (error) {
        const err = error.message || 'Failed to update notes';
        yield put(actions.updateShiftNotesFailure(err));
        SweetAlert.error({ text: err });
    }
}

function* fetchShiftPrivilegesSaga(action) {
    try {
        const { radiologistId, asOfDate } = action.payload;
        const response = yield call(services.getShiftPrivileges, radiologistId, asOfDate);
        if (response?.success) {
            yield put(actions.fetchShiftPrivilegesSuccess(response.value || []));
        } else {
            yield put(actions.fetchShiftPrivilegesFailure(response?.errorMessage || 'Failed to load privileges'));
        }
    } catch (error) {
        yield put(actions.fetchShiftPrivilegesFailure(error.message || 'Failed to load privileges'));
    }
}

function* saveTaskSaga(action) {
    try {
        const response = yield call(services.saveTaskNew, action.payload);
        if (response?.success === false || response?.isSuccess === false) {
            const err = response?.errorMessage || response?.message || 'Failed to create task';
            yield put(actions.saveTaskFailure(err));
            SweetAlert.error({ text: err });
            return;
        }
        const code = response?.value ?? response?.values;
        if (code === 2) {
            const msg = 'A task with this name already exists.';
            yield put(actions.saveTaskFailure(msg));
            SweetAlert.error({ text: msg });
            return;
        }
        yield put(actions.saveTaskSuccess({ TaskName: action.payload.TaskName }));
        SweetAlert.success({ text: 'Task created.' });
        yield put(actions.fetchScheduleDropdownsRequest());
    } catch (error) {
        const err = error.message || 'Failed to create task';
        yield put(actions.saveTaskFailure(err));
        SweetAlert.error({ text: err });
    }
}

function* fetchTaskDetailSaga(action) {
    try {
        const response = yield call(services.getTaskById, action.payload);
        if (response?.success) {
            const task = response.values || response.value || null;
            yield put(actions.fetchTaskDetailSuccess(task));
        } else {
            const err = response?.errorMessage || 'Failed to load task details';
            yield put(actions.fetchTaskDetailFailure(err));
        }
    } catch (error) {
        yield put(actions.fetchTaskDetailFailure(error.message || 'Failed to load task details'));
    }
}

function* fetchRadAssociatedSitesSaga(action) {
    try {
        const { radiologistId, asOfDate } = action.payload;
        const response = yield call(services.getRadAssociatedSites, radiologistId, asOfDate);
        if (response?.success) {
            yield put(actions.fetchRadAssociatedSitesSuccess(response.values || response.value || []));
        } else {
            yield put(actions.fetchRadAssociatedSitesFailure(response?.errorMessage || 'Failed to load associated sites'));
        }
    } catch (error) {
        yield put(actions.fetchRadAssociatedSitesFailure(error.message || 'Failed to load associated sites'));
    }
}

export default function* scheduleViewerSaga() {
    yield takeEvery(types.FETCH_SHIFTS_REQUEST, fetchShiftsSaga);
    yield takeEvery(types.FETCH_SCHEDULE_DROPDOWNS_REQUEST, fetchScheduleDropdownsSaga);
    yield takeEvery(types.SAVE_SHIFT_REQUEST, saveShiftSaga);
    yield takeEvery(types.SAVE_SHIFT_BULK_REQUEST, saveShiftBulkSaga);
    yield takeLatest(types.FETCH_RADS_ASSOCIATED_SITES_REQUEST, fetchRadsAssociatedSitesSaga);
    yield takeEvery(types.DELETE_SHIFT_REQUEST, deleteShiftSaga);
    yield takeEvery(types.UPDATE_SHIFT_GROUP_REQUEST, updateShiftGroupSaga);
    yield takeEvery(types.SWAP_SHIFT_REQUEST, swapShiftSaga);
    yield takeEvery(types.SWAP_SHIFT_GROUP_REQUEST, swapShiftGroupSaga);
    yield takeEvery(types.DELETE_SHIFT_GROUP_REQUEST, deleteShiftGroupSaga);
    yield takeEvery(types.UPDATE_SHIFT_NOTES_REQUEST, updateShiftNotesSaga);
    yield takeLatest(types.FETCH_SHIFT_PRIVILEGES_REQUEST, fetchShiftPrivilegesSaga);
    yield takeLatest(types.FETCH_RAD_ASSOCIATED_SITES_REQUEST, fetchRadAssociatedSitesSaga);
    yield takeEvery(types.SAVE_TASK_REQUEST, saveTaskSaga);
    yield takeLatest(types.FETCH_TASK_DETAIL_REQUEST, fetchTaskDetailSaga);
}
